import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AccessibilityRuntime from "@/components/AccessibilityRuntime";
import AnalyticsTracker from "@/components/AnalyticsTracker";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import DashboardBrowseOpportunities from "@/pages/DashboardBrowseOpportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import DashboardHome from "@/pages/DashboardHome";
import OpportunitiesAdmin from "@/pages/admin/OpportunitiesAdmin";
import ApplicationsAdmin from "@/pages/admin/ApplicationsAdmin";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import CertificatesAdmin from "@/pages/admin/CertificatesAdmin";
import ContactRequestsAdmin from "@/pages/admin/ContactRequestsAdmin";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminContracts from "@/pages/admin/AdminContracts";
import AssociationContracts from "@/pages/association/AssociationContracts";
import StudentContracts from "@/pages/student/StudentContracts";
import SurveysAdmin from "@/pages/admin/SurveysAdmin";
import AssociationSurveysAdmin from "@/pages/admin/AssociationSurveysAdmin";
import Support from "@/pages/Support";
import SupportAdmin from "@/pages/admin/SupportAdmin";
import LandingAdmin from "@/pages/admin/LandingAdmin";
import AmbassadorsAdmin from "@/pages/admin/AmbassadorsAdmin";
import LandingPreview from "@/pages/admin/LandingPreview";
import SessionsAdmin from "@/pages/admin/SessionsAdmin";
import RecruiterNominationsAdmin from "@/pages/admin/RecruiterNominationsAdmin";
import OnboardingSurvey from "@/pages/OnboardingSurvey";
import AssociationOnboarding from "@/pages/AssociationOnboarding";
import RecruiterRequests from "@/pages/recruiter/RecruiterRequests";
import FacultyStudentDetail from "@/pages/faculty/FacultyStudentDetail";
import StudentReports from "@/pages/student/StudentReports";
import MentorStudents from "@/pages/mentor/MentorStudents";
import MentorStudentDetail from "@/pages/mentor/MentorStudentDetail";
import AdminComplaints from "@/pages/admin/AdminComplaints";
import AssociationStudents from "@/pages/association/AssociationStudents";
import AssociationStudentPrint from "@/pages/association/AssociationStudentPrint";
import AdminPlacementSettings from "@/pages/admin/AdminPlacementSettings";
import AdminOpportunitiesQueue from "@/pages/admin/AdminOpportunitiesQueue";
import StudentNotifications from "@/pages/student/StudentNotifications";
import StudentContactRequests from "@/pages/student/ContactRequests";
import SupervisionRequests from "@/pages/SupervisionRequests";
import MyApplications from "@/pages/student/MyApplications";
import CoursesList from "@/pages/content/CoursesList";
import CourseBuilder from "@/pages/content/CourseBuilder";
import MyCourses from "@/pages/student/MyCourses";
import CourseViewer from "@/pages/student/CourseViewer";
import MentorSessions from "@/pages/mentor/MentorSessions";
import StudentSessions from "@/pages/student/StudentSessions";
import Certificates from "@/pages/student/Certificates";
import Verify from "@/pages/Verify";
import VerifyContract from "@/pages/VerifyContract";
import AssociationTasks from "@/pages/association/AssociationTasks";
import AssociationOpportunities from "@/pages/association/AssociationOpportunities";
import AssociationProfile from "@/pages/association/AssociationProfile";
import AssociationTimeline from "@/pages/association/AssociationTimeline";
import StudentProfile from "@/pages/student/StudentProfile";
import Chat from "@/pages/Chat";
import FundingRequestForm from "@/pages/donor/FundingRequestForm";
import FundingRequestsList from "@/pages/donor/FundingRequestsList";
import FundingRequestDetails from "@/pages/donor/FundingRequestDetails";
import AdminFundingRequestsList from "@/pages/admin/AdminFundingRequestsList";
import AdminFundingRequestDetails from "@/pages/admin/AdminFundingRequestDetails";
import StudentTasks from "@/pages/student/StudentTasks";
import StudentJourney from "@/pages/student/StudentJourney";
import AssociationJourney from "@/pages/association/AssociationJourney";
import MentorJourney from "@/pages/mentor/MentorJourney";
import FacultyJourney from "@/pages/faculty/FacultyJourney";
import AdminJourney from "@/pages/admin/AdminJourney";
import AdminExceptions from "@/pages/admin/AdminExceptions";
import PlacementCancellations from "@/pages/admin/PlacementCancellations";
import StudentsOverview from "@/pages/admin/StudentsOverview";
import InterventionInbox from "@/pages/InterventionInbox";
import WeeklyPulse from "@/pages/WeeklyPulse";
import PerformanceIndicators from "@/pages/PerformanceIndicators";
import InstitutionalAssessment from "@/pages/InstitutionalAssessment";
import Partners from "@/pages/Partners";
import RecreationWorkspace from "@/pages/RecreationWorkspace";
import StudentEnrichment from "@/pages/student/StudentEnrichment";
import StudentAmbassador from "@/pages/student/StudentAmbassador";
import { Terms, Privacy, Contact, FAQ } from "@/pages/Legal";

function ApplicationsRouter() {
  const { user } = useAuth();
  if (user?.role === "student") return <MyApplications />;
  return <ApplicationsAdmin />;
}

function ContractsRouter() {
  const { user } = useAuth();
  if (user?.role === "student") return <StudentContracts />;
  return <AssociationContracts />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AccessibilityRuntime />
            <AnalyticsTracker />
            <Toaster position="top-center" richColors dir="rtl" toastOptions={{ style: { fontFamily: "Neo Sans Arabic, IBM Plex Sans Arabic, sans-serif" } }} />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/opportunities" element={<OpportunitiesPage />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail />} />
              <Route path="/verify/:cert_id" element={<Verify />} />
              <Route path="/verify-contract/:number" element={<VerifyContract />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />

              <Route path="/dashboard" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
              <Route path="/dashboard/browse-opportunities" element={<ProtectedRoute roles={["student", "association", "mentor"]}><DashboardBrowseOpportunities /></ProtectedRoute>} />
              <Route path="/dashboard/opportunities" element={<ProtectedRoute roles={["super_admin"]}><OpportunitiesAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/linked-opportunities" element={<ProtectedRoute roles={["association"]}><AssociationOpportunities /></ProtectedRoute>} />
              <Route path="/dashboard/association-profile" element={<ProtectedRoute roles={["association"]}><AssociationProfile /></ProtectedRoute>} />
              <Route path="/dashboard/association-timeline" element={<ProtectedRoute roles={["super_admin"]}><AssociationTimeline /></ProtectedRoute>} />
              <Route path="/dashboard/profile" element={<ProtectedRoute roles={["student"]}><StudentProfile /></ProtectedRoute>} />
              <Route path="/dashboard/chat" element={<ProtectedRoute roles={["student", "mentor"]}><Chat /></ProtectedRoute>} />
              <Route path="/dashboard/applications" element={<ProtectedRoute roles={["super_admin", "student", "association", "mentor"]}><ApplicationsRouter /></ProtectedRoute>} />
              <Route path="/dashboard/students-overview" element={<ProtectedRoute roles={["super_admin"]}><StudentsOverview /></ProtectedRoute>} />
              <Route path="/dashboard/placement-cancellations" element={<ProtectedRoute roles={["super_admin"]}><PlacementCancellations /></ProtectedRoute>} />
              <Route path="/dashboard/users" element={<ProtectedRoute roles={["super_admin"]}><UsersAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/certificates-admin" element={<ProtectedRoute roles={["super_admin"]}><CertificatesAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/contact-requests" element={<ProtectedRoute roles={["super_admin", "recruiter", "student"]}><ContactRequestsRouter /></ProtectedRoute>} />
              <Route path="/dashboard/supervision-requests" element={<ProtectedRoute roles={["student", "faculty"]}><SupervisionRequests /></ProtectedRoute>} />
              <Route path="/dashboard/integrations" element={<ProtectedRoute roles={["super_admin"]}><AdminIntegrations /></ProtectedRoute>} />
              <Route path="/dashboard/admin/contracts" element={<ProtectedRoute roles={["super_admin"]}><AdminContracts /></ProtectedRoute>} />
              <Route path="/dashboard/contracts" element={<ProtectedRoute roles={["association", "student"]}><ContractsRouter /></ProtectedRoute>} />
              <Route path="/dashboard/surveys" element={<ProtectedRoute roles={["super_admin","faculty"]}><SurveysAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/association-surveys" element={<ProtectedRoute roles={["super_admin"]}><AssociationSurveysAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/dashboard/support-admin" element={<ProtectedRoute roles={["super_admin"]}><SupportAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/landing" element={<ProtectedRoute roles={["super_admin"]}><LandingAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/ambassadors" element={<ProtectedRoute roles={["super_admin"]}><AmbassadorsAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/landing/preview" element={<ProtectedRoute roles={["super_admin"]}><LandingPreview /></ProtectedRoute>} />
              <Route path="/dashboard/sessions-admin" element={<ProtectedRoute roles={["super_admin"]}><SessionsAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/recruiter-nominations" element={<ProtectedRoute roles={["super_admin"]}><RecruiterNominationsAdmin /></ProtectedRoute>} />
              <Route path="/dashboard/faculty/student/:id" element={<ProtectedRoute roles={["faculty", "super_admin"]}><FacultyStudentDetail /></ProtectedRoute>} />
              <Route path="/dashboard/reports" element={<ProtectedRoute roles={["student"]}><StudentReports /></ProtectedRoute>} />
              <Route path="/dashboard/my-students" element={<ProtectedRoute roles={["mentor", "super_admin"]}><MentorStudents /></ProtectedRoute>} />
              <Route path="/dashboard/mentor/student/:id" element={<ProtectedRoute roles={["mentor", "super_admin"]}><MentorStudentDetail /></ProtectedRoute>} />
              <Route path="/dashboard/complaints-admin" element={<ProtectedRoute roles={["super_admin"]}><AdminComplaints /></ProtectedRoute>} />
              <Route path="/dashboard/students-directory" element={<ProtectedRoute roles={["association", "super_admin"]}><AssociationStudents /></ProtectedRoute>} />
              <Route path="/dashboard/students-directory/:id/print" element={<ProtectedRoute roles={["association", "super_admin"]}><AssociationStudentPrint /></ProtectedRoute>} />
              <Route path="/dashboard/placement-settings" element={<ProtectedRoute roles={["super_admin"]}><AdminPlacementSettings /></ProtectedRoute>} />
              <Route path="/dashboard/opportunities-queue" element={<ProtectedRoute roles={["super_admin"]}><AdminOpportunitiesQueue /></ProtectedRoute>} />
              <Route path="/dashboard/admin/exceptions" element={<ProtectedRoute roles={["super_admin"]}><AdminExceptions /></ProtectedRoute>} />
              <Route path="/dashboard/notifications" element={<ProtectedRoute roles={["student"]}><StudentNotifications /></ProtectedRoute>} />
              <Route path="/dashboard/funding/requests" element={<ProtectedRoute roles={["donor"]}><FundingRequestsList /></ProtectedRoute>} />
              <Route path="/dashboard/funding/requests/new" element={<ProtectedRoute roles={["donor"]}><FundingRequestForm /></ProtectedRoute>} />
              <Route path="/dashboard/funding/requests/:id/edit" element={<ProtectedRoute roles={["donor"]}><FundingRequestForm /></ProtectedRoute>} />
              <Route path="/dashboard/funding/requests/:id" element={<ProtectedRoute roles={["donor"]}><FundingRequestDetails /></ProtectedRoute>} />
              <Route path="/dashboard/admin/funding/requests" element={<ProtectedRoute roles={["super_admin"]}><AdminFundingRequestsList /></ProtectedRoute>} />
              <Route path="/dashboard/admin/funding/requests/:id" element={<ProtectedRoute roles={["super_admin"]}><AdminFundingRequestDetails /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute roles={["student"]}><OnboardingSurvey /></ProtectedRoute>} />
              <Route path="/onboarding/association" element={<ProtectedRoute roles={["association"]}><AssociationOnboarding /></ProtectedRoute>} />

              <Route path="/dashboard/enrichment" element={<ProtectedRoute roles={["recreation_mentor"]}><RecreationWorkspace /></ProtectedRoute>} />
              <Route path="/dashboard/my-enrichment" element={<ProtectedRoute roles={["student"]}><StudentEnrichment /></ProtectedRoute>} />
              <Route path="/dashboard/my-ambassador" element={<ProtectedRoute roles={["student"]}><StudentAmbassador /></ProtectedRoute>} />
              <Route path="/dashboard/courses" element={<ProtectedRoute roles={["super_admin", "content_creator"]}><CoursesList /></ProtectedRoute>} />
              <Route path="/dashboard/courses/:id" element={<ProtectedRoute roles={["super_admin", "content_creator"]}><CourseBuilder /></ProtectedRoute>} />

              <Route path="/dashboard/my-courses" element={<ProtectedRoute roles={["student"]}><MyCourses /></ProtectedRoute>} />
              <Route path="/dashboard/courses/:id/learn" element={<ProtectedRoute roles={["student"]}><CourseViewer /></ProtectedRoute>} />
              <Route path="/dashboard/certificates" element={<ProtectedRoute roles={["student", "association"]}><Certificates /></ProtectedRoute>} />

              <Route path="/dashboard/sessions" element={<ProtectedRoute roles={["mentor", "student", "association", "super_admin"]}><SessionsRouter /></ProtectedRoute>} />

              <Route path="/dashboard/tasks" element={<ProtectedRoute roles={["association", "student", "super_admin"]}><TasksRouter /></ProtectedRoute>} />
              <Route path="/dashboard/interventions" element={<ProtectedRoute roles={["super_admin", "student", "association", "mentor", "faculty"]}><InterventionInbox /></ProtectedRoute>} />
              <Route path="/dashboard/weekly-pulse" element={<ProtectedRoute roles={["super_admin", "student", "association", "mentor", "faculty"]}><WeeklyPulse /></ProtectedRoute>} />
              <Route path="/dashboard/performance-indicators" element={<ProtectedRoute roles={["super_admin", "student", "association", "mentor", "donor"]}><PerformanceIndicators /></ProtectedRoute>} />
              <Route path="/dashboard/institutional-assessment" element={<ProtectedRoute roles={["super_admin", "association"]}><InstitutionalAssessment /></ProtectedRoute>} />
              <Route path="/dashboard/journey" element={<ProtectedRoute roles={["student"]}><StudentJourney /></ProtectedRoute>} />
              <Route path="/dashboard/journeys/:applicationId" element={<ProtectedRoute roles={["student"]}><StudentJourney /></ProtectedRoute>} />
              <Route path="/dashboard/association/journeys/:applicationId" element={<ProtectedRoute roles={["association"]}><AssociationJourney /></ProtectedRoute>} />
              <Route path="/dashboard/mentor/journeys/:applicationId" element={<ProtectedRoute roles={["mentor"]}><MentorJourney /></ProtectedRoute>} />
              <Route path="/dashboard/faculty/journeys/:applicationId" element={<ProtectedRoute roles={["faculty"]}><FacultyJourney /></ProtectedRoute>} />
              <Route path="/dashboard/admin/journeys/:applicationId" element={<ProtectedRoute roles={["super_admin"]}><AdminJourney /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

function SessionsRouter() {
  const { user } = useAuth();
  if (["student", "association"].includes(user?.role)) return <StudentSessions />;
  return <MentorSessions />;
}

function TasksRouter() {
  const { user } = useAuth();
  if (user?.role === "student") return <StudentTasks />;
  return <AssociationTasks />;
}

function ContactRequestsRouter() {
  const { user } = useAuth();
  if (user?.role === "recruiter") return <RecruiterRequests />;
  if (user?.role === "student") return <StudentContactRequests />;
  return <ContactRequestsAdmin />;
}

export default App;
